/* eslint-disable @typescript-eslint/no-explicit-any -- error toasts; see mem://preferences/lint-debt.md */
import { useEffect, useState } from "react";
import { Check, Lock, Pencil, Plus, Tag, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  type PublishType,
  createPublishType,
  deletePublishType,
  listPublishTypes,
  renamePublishType,
} from "@/lib/workspacePublishTypes";

type Props = {
  workspaceId: string;
  onChanged?: (types: PublishType[]) => void;
};

export const CorpusPublishTypesManager = ({ workspaceId, onChanged }: Props) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState<PublishType[]>([]);
  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PublishType | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const rows = await listPublishTypes(workspaceId);
      setTypes(rows);
      onChanged?.(rows);
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to load publish types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspaceId]);

  const handleAdd = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setBusy(true);
    try {
      await createPublishType({ workspaceId, label, position: types.length });
      setNewLabel("");
      await reload();
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to add publish type");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (t: PublishType) => {
    setEditingId(t.id);
    setEditingLabel(t.label);
  };

  const commitEdit = async () => {
    if (!editingId) return;
    const label = editingLabel.trim();
    if (!label) {
      setEditingId(null);
      return;
    }
    setBusy(true);
    try {
      await renamePublishType(editingId, label);
      setEditingId(null);
      await reload();
    } catch (err: any) {
      toast.error(err?.message ?? "Rename failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deletePublishType(deleteTarget.id);
      setDeleteTarget(null);
      await reload();
      toast.success("Publish type removed");
    } catch (err: any) {
      toast.error(err?.message ?? "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label="Manage publish types"
            className="h-7 gap-1.5 px-2 text-xs"
          >
            <Tag className="h-3.5 w-3.5" />
            Manage publish types
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-2">
          <div className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Publish types
          </div>
          <p className="mb-2 px-1 text-[11px] leading-snug text-muted-foreground">
            Publish types describe the kinds of content you create (Article, Newsletter, etc.).
            Tag any Corpus doc with its type so the AI knows what it is. To make a doc a
            Writing Style Sample for a type, star that tag on the doc.
          </p>
          {loading ? (
            <div className="p-2 text-xs text-muted-foreground">Loading…</div>
          ) : (
            <ul className="max-h-72 space-y-0.5 overflow-y-auto">
              {types.map((t) => {
                const isEditing = editingId === t.id;
                return (
                  <li
                    key={t.id}
                    className="flex items-center gap-1 rounded px-1.5 py-1 hover:bg-muted/60"
                  >
                    {isEditing ? (
                      <>
                        <Input
                          value={editingLabel}
                          onChange={(e) => setEditingLabel(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") void commitEdit();
                            if (e.key === "Escape") setEditingId(null);
                          }}
                          autoFocus
                          className="h-7 flex-1 text-sm"
                          disabled={busy}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={() => void commitEdit()}
                          disabled={busy}
                          aria-label="Save"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={() => setEditingId(null)}
                          disabled={busy}
                          aria-label="Cancel"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 truncate text-sm">{t.label}</span>
                        {t.is_seed && (
                          <Lock
                            className="h-3 w-3 text-muted-foreground"
                            aria-label="Built-in type — cannot be deleted"
                          />
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 text-muted-foreground hover:text-foreground"
                          onClick={() => startEdit(t)}
                          aria-label={`Rename ${t.label}`}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        {!t.is_seed && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteTarget(t)}
                            aria-label={`Delete ${t.label}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </>
                    )}
                  </li>
                );
              })}
              {types.length === 0 && (
                <li className="p-2 text-xs text-muted-foreground">
                  No publish types yet.
                </li>
              )}
            </ul>
          )}
          <div className="mt-2 flex items-center gap-1 border-t border-border pt-2">
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder='e.g. "Talk script"'
              className="h-7 flex-1 text-sm"
              disabled={busy}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleAdd();
              }}
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => void handleAdd()}
              disabled={busy || !newLabel.trim()}
              aria-label="Add publish type"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove publish type "{deleteTarget?.label}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Corpus documents tagged with this type will be untagged. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
